import { Col, Form, Row } from "antd";
import apiFactory from "api";
import Input from "component/admin/input";
import { ButtonOutline, ButtonSolid } from "component/button";
import { isNumber } from "lodash";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IProfile } from "types/admin";
import {
  formatNumberFullWidthToHalfWidth,
  isValidFullOrHalfWidthNumber,
} from "utils/formats";

export default function ProfileDetail() {
  const [disabled, setDisable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [form] = Form.useForm();
  const [data, setData] = useState<IProfile | {}>({});
  const fetchData = async () => {
    try {
      const result = await apiFactory.profileAdminApi.getServiceDetail();
      if (result) {
        const data = { ...result };
        form.setFieldsValue(data);
        setData(data);
      }
    } catch (error: any) {
      toast.error(error?.message);
    }
  };

  const onFinish = async (values: any) => {
    try {
      const valuesClone = { ...values };
      valuesClone.day_payment = valuesClone.day_payment
        ? Number(formatNumberFullWidthToHalfWidth(valuesClone.day_payment))
        : null;
        
      valuesClone.rate_registration_disability = isNumber(
        valuesClone.rate_registration_disability
      )
        ? valuesClone.rate_registration_disability
        : valuesClone.rate_registration_disability
        ? Number(
            formatNumberFullWidthToHalfWidth(
              valuesClone.rate_registration_disability
            )
          )
        : null;

      valuesClone.rate_registration_nursing_care = isNumber(
        valuesClone.rate_registration_nursing_care
      )
        ? valuesClone.rate_registration_nursing_care
        : valuesClone.rate_registration_nursing_care
        ? Number(
            formatNumberFullWidthToHalfWidth(
              valuesClone.rate_registration_nursing_care
            )
          )
        : null;

      setSubmitting(true);
      await apiFactory.profileAdminApi.updateProfile(valuesClone);
      setDisable(true);
      toast.success("更新に成功しました。");
      fetchData();
    } catch (err) {
      toast.error("保存が失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFieldsChange = () => {
    const errors = form.getFieldsError();
    const hasAnyError = errors.some((field) => field.errors.length > 0);
    setHasError(hasAnyError);
  };
  return (
    <div>
      <div className="page-title">管理メニュー　プロフィール</div>
      <div className="page-container">
        {disabled && (
          <div className="text-end mb-[30px]">
            <ButtonSolid
              className="!h-[35px] !px-[54px] !text-[14px] !leading-[24px]"
              onClick={() => setDisable(!disabled)}
            >
              編集
            </ButtonSolid>
          </div>
        )}
        <Form
          initialValues={data}
          onFinish={onFinish}
          form={form}
          disabled={disabled}
          onFieldsChange={handleFieldsChange}
        >
          <Row>
            <Col span={11}>
              <Form.Item label="施設ID" name="id">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="施設名" name="facility_name">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="郵便番号" name="zipcode">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="都道府県" name="district">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="市区町村" name="city">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="町名・番地" name="street">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="ビル・マンション名" name="building_name">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="電話番号" name="mobile_phone">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="FAX" name="fax">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="事業者番号" name="business_number">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item label="等級登録" name="grade_registration">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item
                label="掛け率登録（介護）"
                name="rate_registration_nursing_care"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (!isValidFullOrHalfWidthNumber(value))
                          return Promise.reject(
                            new Error("数値または小数のみ入力してください。")
                          );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <Form.Item
                label="掛け率登録（障害）"
                name="rate_registration_disability"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (!isValidFullOrHalfWidthNumber(value))
                          return Promise.reject(
                            new Error("数値または小数のみ入力してください。")
                          );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={11}>
              <Form.Item
                label="請求日"
                name="day_payment"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value) {
                        const valueHalf = Number(
                          formatNumberFullWidthToHalfWidth(value)
                        );
                        if (!valueHalf || valueHalf === 0 || valueHalf > 31)
                          return Promise.reject(
                            new Error("日付は1から31の範囲で入力してください。")
                          );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {!disabled && (
          <div className="flex items-center justify-between">
            <div className="flex items-center text-[12px] leading-[20px] gap-x-[6px]"></div>
            <div className="flex gap-x-[8px]">
              <ButtonOutline
                className="!h-[35px] !px-[16px] !text-[14px] !leading-[24px]"
                onClick={() => {
                  setDisable(true);
                  form.setFieldsValue(data);
                }}
              >
                キャンセル
              </ButtonOutline>
              <ButtonSolid
                className="!h-[35px] !px-[54px] !text-[14px] !leading-[24px]"
                onClick={form.submit}
                isLoading={submitting}
                isDisabled={hasError}
              >
                保存
              </ButtonSolid>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
